import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@shared/services/apiClient";

type RecentStudyLike = {
  id?: string;
  study_id?: string;
  studyId?: string;
  recording_id?: string;
  recordingId?: string;
  patient_id?: string;
  patientId?: string;
};

function extractRecentStudies(payload: unknown): RecentStudyLike[] {
  const body = payload as
    | {
        data?: unknown;
        studies?: unknown;
      }
    | RecentStudyLike[]
    | null
    | undefined;
  const data = Array.isArray((body as { data?: unknown })?.data)
    ? (body as { data: RecentStudyLike[] }).data
    : (body as { data?: { studies?: unknown } })?.data;
  const studies = Array.isArray(data)
    ? data
    : Array.isArray((data as { studies?: unknown })?.studies)
      ? (data as { studies: RecentStudyLike[] }).studies
      : Array.isArray((body as { studies?: unknown })?.studies)
        ? (body as { studies: RecentStudyLike[] }).studies
        : Array.isArray(body)
          ? body
          : [];
  return studies.filter(Boolean);
}

const ViewerRedirectPage: React.FC = () => {
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );

  const explicitStudyId =
    params.get("studyId") ||
    params.get("loadStudy") ||
    params.get("eegStudyId");
  const explicitRecordingId = params.get("recordingId") || params.get("id");

  const { data: fallbackStudy, isLoading } = useQuery({
    queryKey: ["viewer-redirect-recent-study"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/recent-studies?limit=1");
      return extractRecentStudies(response.data)[0] || null;
    },
    enabled: !explicitStudyId,
    staleTime: 60_000,
  });

  const resolvedStudyId =
    explicitStudyId || fallbackStudy?.study_id || fallbackStudy?.studyId || "";
  const resolvedRecordingId =
    explicitRecordingId ||
    fallbackStudy?.recording_id ||
    fallbackStudy?.recordingId ||
    fallbackStudy?.id ||
    "";
  const resolvedPatientId =
    params.get("patientId") ||
    fallbackStudy?.patient_id ||
    fallbackStudy?.patientId ||
    "";

  if (!resolvedStudyId) {
    if (isLoading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      );
    }
    return <Navigate to="/patients" replace />;
  }

  const nextParams = new URLSearchParams();
  if (resolvedRecordingId) nextParams.set("recordingId", resolvedRecordingId);
  if (resolvedPatientId) nextParams.set("patientId", resolvedPatientId);
  if (params.get("openViewerVisualControls") === "1") {
    nextParams.set("openViewerVisualControls", "1");
  }

  const query = nextParams.toString();
  return (
    <Navigate
      to={`/eeg/${resolvedStudyId}${query ? `?${query}` : ""}`}
      replace
    />
  );
};

export default ViewerRedirectPage;
